import { motion } from 'framer-motion';
import { Users, ClipboardList, Calendar, MessageSquare, UserCog, Target, BarChart3, Clock } from 'lucide-react';
import { useWebsite } from '../../contexts/WebsiteContext';

const clientFeatures = [
  {
    icon: ClipboardList,
    title: "Client Profiles",
    description: "Detailed client preferences, vehicle history, and service records in one place"
  },
  {
    icon: Calendar,
    title: "Booking Management",
    description: "Efficient scheduling system for services, maintenance, and special requests"
  },
  {
    icon: MessageSquare,
    title: "Communication Hub",
    description: "Direct messaging and automated notifications for seamless client interaction"
  }
];

const staffFeatures = [
  {
    icon: Target,
    title: "Task Assignment",
    description: "Smart workload distribution and real-time task tracking system"
  },
  {
    icon: BarChart3,
    title: "Performance Monitoring",
    description: "Track staff performance, certifications, and customer satisfaction metrics"
  },
  {
    icon: Clock,
    title: "Shift Management",
    description: "Flexible scheduling system with automated conflict resolution"
  }
];

export default function ClientStaffManagement() {
  const { /* isSectionVisible, */ } = useWebsite();

  return (
    <section className="py-24 bg-gradient-to-b from-black to-zinc-900">
      <div className="max-w-7xl mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-amber-400 mb-4">
            Seamless Client & Staff Management
          </h2>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            Streamline your operations with our comprehensive client and staff management
            tools designed specifically for luxury automotive services.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-16">
          {/* Client Management */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-4 mb-8">
              <Users className="w-8 h-8 text-amber-400" />
              <h3 className="text-2xl font-semibold text-white">Client Management</h3>
            </div>
            <div className="space-y-6">
              {clientFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-6 rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-amber-500/30"
                >
                  <feature.icon className="w-6 h-6 text-amber-400 mb-2" />
                  <h4 className="text-lg font-semibold text-white mb-2">{feature.title}</h4>
                  <p className="text-gray-400">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Staff Management */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-4 mb-8">
              <UserCog className="w-8 h-8 text-amber-400" />
              <h3 className="text-2xl font-semibold text-white">Staff Management</h3>
            </div>
            <div className="space-y-6">
              {staffFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-6 rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-amber-500/30"
                >
                  <feature.icon className="w-6 h-6 text-amber-400 mb-2" />
                  <h4 className="text-lg font-semibold text-white mb-2">{feature.title}</h4>
                  <p className="text-gray-400">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
} 